/**
 * Shown before a Meta login when the page runs in an in-app browser (lib/in-app-browser.ts). Suggests opening the
 * page in Safari or Chrome, offers to copy the link, and still lets the user continue. Text comes from the caller,
 * so each screen keeps its own EN/AR copy.
 */
import { useId, useState } from 'react';
import { Button, Text } from '@qm/ui';

export interface InAppBrowserCopy {
  title: string;
  body: string;
  copyLink: string;
  copied: string;
  copyManual: string;
  continueAnyway: string;
}

export function InAppBrowserNotice({ copy, onContinue, url }: { copy: InAppBrowserCopy; onContinue: () => void; url?: string }) {
  const link = url ?? (typeof window === 'undefined' ? '' : window.location.href);
  const [copied, setCopied] = useState<'idle' | 'done' | 'manual'>('idle');
  const titleId = useId();
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied('done');
    } catch {
      setCopied('manual'); // clipboard blocked in this webview: show the link to copy by hand
    }
  };
  return (
    <div className="qm-in-app-notice" role="alert" aria-labelledby={titleId} data-testid="in-app-browser-notice">
      <Text id={titleId}><strong>{copy.title}</strong></Text>
      <Text>{copy.body}</Text>
      <div className="qm-connection-buttons">
        <Button variant="primary" onClick={() => void copyLink()}>{copy.copyLink}</Button>
        <Button onClick={onContinue}>{copy.continueAnyway}</Button>
      </div>
      {copied === 'done' ? <Text role="status">{copy.copied}</Text> : null}
      {copied === 'manual' ? (
        <label>
          <span className="qm-text" data-tone="muted">{copy.copyManual}</span>
          <input type="text" readOnly value={link} dir="ltr" style={{ width: '100%', boxSizing: 'border-box' }} onFocus={(e) => e.currentTarget.select()} />
        </label>
      ) : null}
    </div>
  );
}
